import { useState, useRef, useEffect } from "react";
import {
  X, UploadCloud, FileText, Loader2, ShieldAlert, ScanSearch, CheckCircle2, Ban,
  Pencil, AlertTriangle, Info, Sparkles, ListPlus, Users, Trash2, ExternalLink,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUniversalIntake } from "@/context/UniversalIntakeContext";
import { useRestrictedVault } from "@/context/RestrictedVaultContext";
import { toast } from "sonner";
import { defaultUniversalIntakeLinkMode } from "@/config/universalIntakeLinkMode.mjs";

const CONF = {
  High: "bg-emerald-500/15 text-emerald-300 ring-emerald-400/30",
  Medium: "bg-amber-500/15 text-amber-300 ring-amber-400/30",
  "Needs Review": "bg-rose-500/15 text-rose-300 ring-rose-400/30",
};

export function UniversalIntake() {
  const { user } = useAuth();
  const { open, clientId, leadId, closeIntake } = useUniversalIntake();
  const { openVault } = useRestrictedVault();
  const [step, setStep] = useState("input"); // input | analyzing | review | done
  const [approvedClientId, setApprovedClientId] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState([]);
  const [ack, setAck] = useState(false);
  const [intakeId, setIntakeId] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const [fieldState, setFieldState] = useState({}); // idx -> {status, value}
  const [tasks, setTasks] = useState([]);
  const [linkMode, setLinkMode] = useState(() => defaultUniversalIntakeLinkMode(clientId));
  const [matchId, setMatchId] = useState("");
  const [saveNote, setSaveNote] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showHandling, setShowHandling] = useState(false);
  const fileRef = useRef();
  const isAdmin = ["admin", "ops_manager"].includes(user?.role);

  useEffect(() => {
    if (open) {
      setStep("input"); setNotes(""); setFiles([]); setAck(false); setIntakeId("");
      setAnalysis(null); setFieldState({}); setTasks([]); setSaveNote(false);
      setLinkMode(defaultUniversalIntakeLinkMode(clientId)); setMatchId(clientId || "");
    }
  }, [open, clientId]);

  if (!open) return null;

  const addFiles = (list) => {
    const arr = Array.from(list || []);
    const big = arr.find((f) => f.size > 12 * 1024 * 1024);
    if (big) toast.warning(`${big.name} is large (>12MB) and may take longer or be rejected.`);
    setFiles((prev) => [...prev, ...arr]);
  };

  const analyze = async () => {
    if (!notes.trim() && files.length === 0) {
      toast.error("Paste notes or add a file first."); return;
    }
    if (!ack) { toast.error("Please acknowledge the AI Data Handling notice."); return; }
    setStep("analyzing"); setBusy(true);
    try {
      const fd = new FormData();
      fd.append("notes", notes);
      fd.append("source_type", "internal");
      if (clientId) fd.append("link_client_id", clientId);
      if (leadId) fd.append("link_lead_id", leadId);
      files.forEach((f) => fd.append("files", f));
      const { data: created } = await api.post("/universal-intake", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setIntakeId(created.id);
      const stored = created.files || [];
      const unanalyzable = stored.filter((f) => !f.analyzable);
      if (unanalyzable.length) toast.info(unanalyzable[0].note || "Some files stored without analysis.");
      const { data } = await api.post(`/universal-intake/${created.id}/analyze`);
      setAnalysis(data);
      const fs = {};
      (data.fields || []).forEach((f, i) => { fs[i] = { status: "accept", value: f.value }; });
      setFieldState(fs);
      setTasks((data.tasks || []).map((t) => ({ ...t, include: true, assigned_to: user.name, due_date: t.due_date || "" })));
      setStep("review");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Analysis failed");
      setStep("input");
    } finally { setBusy(false); }
  };

  const setField = (i, patch) => setFieldState((s) => ({ ...s, [i]: { ...s[i], ...patch } }));

  const approve = async () => {
    const approved_fields = (analysis.fields || [])
      .map((f, i) => ({ f, st: fieldState[i] }))
      .filter((x) => x.st?.status === "accept" || x.st?.status === "edit")
      .map((x) => ({ field: x.f.field, label: x.f.label, value: x.st.value, category: x.f.category }));
    const chosenTasks = tasks.filter((t) => t.include);
    const missingDue = chosenTasks.find((t) => !t.due_date);
    if (missingDue) { toast.error(`Select a due date for: ${missingDue.title}`); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/universal-intake/${intakeId}/approve`, {
        link_mode: linkMode,
        client_id: linkMode === "existing" ? matchId : "",
        approved_fields,
        tasks: chosenTasks.map((t) => ({
          title: t.title, task_type: t.task_type, reason: t.reason,
          due_date: t.due_date, assigned_to: t.assigned_to, priority: t.priority || "Medium", note: t.note || "",
        })),
        save_original_note: saveNote,
        instabrain_flag: !!analysis.instabrain_recommended,
      });
      toast.success(`Approved — ${data.changes.length} field(s), ${data.tasks.length} task(s) saved.`);
      setApprovedClientId(data.client_id || "");
      setStep("done");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Approve failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="universal-intake-modal">
      <div className="absolute inset-0 bg-[#0a1826]/70 backdrop-blur-sm" onClick={closeIntake} />
      <div className="absolute inset-x-0 top-0 mx-auto flex h-full w-full max-w-3xl flex-col bg-[#0f2439] text-slate-100 shadow-2xl sm:inset-y-4 sm:h-auto sm:max-h-[calc(100vh-2rem)] sm:rounded-2xl">
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-white/10 px-5 py-4">
          <ScanSearch className="h-5 w-5 text-[#B28A3D]" />
          <div className="font-heading font-bold">Universal Intake</div>
          <span className="rounded-full bg-[#B28A3D]/20 px-2 py-0.5 text-[10px] font-semibold text-[#d9b978]">v1</span>
          <div className="flex-1" />
          <button onClick={closeIntake} className="text-slate-300 hover:text-white" data-testid="ui-close"><X className="h-5 w-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* STEP: INPUT */}
          {step === "input" && (
            <>
              <p className="text-sm text-slate-300">
                Paste notes or upload client-related documents or spreadsheets. I'll organize the information, identify possible missing
                items, and suggest structured updates and follow-up tasks. <strong>Nothing changes until you approve it.</strong>
              </p>

              <div className="rounded-lg border border-[#B28A3D]/30 bg-[#B28A3D]/5 p-3 text-[12px] text-slate-300">
                <button onClick={() => setShowHandling((v) => !v)} className="flex items-center gap-1.5 font-semibold text-[#d9b978]" data-testid="ui-handling-toggle">
                  <Info className="h-4 w-4" /> AI Data Handling notice {showHandling ? "▾" : "▸"}
                </button>
                {showHandling && (
                  <ul className="mt-2 list-disc space-y-1 pl-5">
                    <li>Sensitive numbers (SSN, bank/routing, cards, passwords) are <strong>redacted locally first</strong> and never sent.</li>
                    <li>Only the redacted content is sent to <strong>OpenAI GPT-4o</strong> for structured extraction.</li>
                    <li>No client record changes until you review and approve.</li>
                    <li>Original files stay permission-restricted in Fortunate Operations; vendors have no access.</li>
                  </ul>
                )}
                <label className="mt-2 flex items-center gap-2">
                  <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} data-testid="ui-ack" />
                  <span>I understand how this content is processed.</span>
                </label>
              </div>

              <textarea
                data-testid="ui-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={7}
                placeholder="Paste notes or email content…"
                className="w-full rounded-lg bg-white/95 p-3 text-sm text-slate-900" />

              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                onClick={() => fileRef.current?.click()}
                className="grid cursor-pointer place-items-center rounded-lg border-2 border-dashed border-white/20 bg-white/5 p-6 text-center hover:border-[#B28A3D]/50"
                data-testid="ui-dropzone">
                <UploadCloud className="mb-1 h-6 w-6 text-[#B28A3D]" />
                <div className="text-sm">Drag &amp; drop, or click to choose files</div>
                <div className="text-[11px] text-slate-400">PDF · PNG · JPG · WEBP · TXT · CSV · XLSX (max 12MB)</div>
                <div className="mt-1 text-[10px] text-slate-500">CSV/XLSX can be analyzed here. For a bulk lead list that should create many pipeline records, use Import Data.</div>
                <input ref={fileRef} type="file" multiple hidden accept=".pdf,.png,.jpg,.jpeg,.webp,.txt,.csv,.xlsx"
                  onChange={(e) => addFiles(e.target.files)} data-testid="ui-file-input" />
              </div>
              {files.length > 0 && (
                <div className="space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md bg-white/5 px-3 py-1.5 text-[12px]">
                      <FileText className="h-3.5 w-3.5 text-slate-400" /><span className="flex-1 truncate">{f.name}</span>
                      <button onClick={() => setFiles((p) => p.filter((_, k) => k !== i))} className="text-slate-400 hover:text-rose-300"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={analyze} disabled={busy}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-[#B28A3D] px-4 py-3 text-sm font-semibold text-[#17324D] hover:bg-[#c99b45] disabled:opacity-60"
                data-testid="ui-analyze-btn">
                <Sparkles className="h-4 w-4" /> Analyze Intake
              </button>
            </>
          )}

          {/* STEP: ANALYZING */}
          {step === "analyzing" && (
            <div className="grid place-items-center gap-3 py-20 text-center" data-testid="ui-analyzing">
              <Loader2 className="h-7 w-7 animate-spin text-[#B28A3D]" />
              <div className="text-sm text-slate-300">Redacting sensitive data, then analyzing with GPT-4o…</div>
            </div>
          )}

          {/* STEP: REVIEW */}
          {step === "review" && analysis && (
            <div className="space-y-4" data-testid="ui-review">
              {analysis.summary && <div className="rounded-lg bg-white/5 p-3 text-sm text-slate-200">{analysis.summary}</div>}

              {analysis.sensitive_detected && (
                <div className="flex items-start gap-2 rounded-lg border border-rose-400/40 bg-rose-500/10 p-3 text-[12px]" data-testid="ui-sensitive">
                  <ShieldAlert className="mt-0.5 h-4 w-4 text-rose-300" />
                  <div>
                    <div className="font-semibold text-rose-200">Sensitive information detected</div>
                    <div className="text-slate-300">Full values were redacted and never stored. Only safe flags are saved:
                      SSN on file: {analysis.sensitive_summary?.ssn_on_file}, Bank on file: {analysis.sensitive_summary?.bank_information_on_file}
                      {analysis.sensitive_summary?.masked_last_four ? `, last four ••${analysis.sensitive_summary.masked_last_four}` : ""}.</div>
                  </div>
                </div>
              )}

              {analysis.high_risk?.detected && (
                <div className="rounded-lg border border-[#B28A3D]/40 bg-[#B28A3D]/10 p-3" data-testid="ui-high-risk">
                  <div className="flex items-center gap-1.5 font-semibold text-[#e7c987]"><AlertTriangle className="h-4 w-4" /> High-Risk Placement Review</div>
                  <div className="mt-1 text-[12px] text-slate-300">Indicators: {(analysis.high_risk.conditions || []).join(", ") || "—"}</div>
                  <p className="mt-1 text-[12px] text-slate-300">This case may require Guaranteed Issue, graded benefit, simplified issue, or alternative placement review. Confirm through InstaBrain, current carrier guidelines, or FMO support before presenting options.</p>
                </div>
              )}

              {/* Client link / matches */}
              <div className="rounded-lg border border-white/10 bg-white/5 p-3">
                <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold"><Users className="h-4 w-4 text-[#B28A3D]" /> Link this intake</div>
                {(analysis.possible_matches || []).length > 0 && (
                  <div className="mb-2 space-y-1" data-testid="ui-matches">
                    <div className="text-[11px] text-amber-300">Possible existing matches (never merged automatically):</div>
                    {analysis.possible_matches.map((m) => (
                      <label key={m.id} className="flex items-center gap-2 rounded-md bg-white/5 px-2 py-1 text-[12px]">
                        <input type="radio" name="ui-match" checked={linkMode === "existing" && matchId === m.id}
                          onChange={() => { setLinkMode("existing"); setMatchId(m.id); }} />
                        <span className="flex-1">{m.name} <span className="text-slate-400">· {m.type} · matched on {(m.matched_on || []).join(", ")}</span></span>
                      </label>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap gap-3 text-[12px]">
                  <label className="flex items-center gap-1.5"><input type="radio" name="ui-link" checked={linkMode === "new"} onChange={() => setLinkMode("new")} data-testid="ui-link-new" /> Create new client candidate</label>
                  <label className="flex items-center gap-1.5"><input type="radio" name="ui-link" checked={linkMode === "none"} onChange={() => setLinkMode("none")} data-testid="ui-link-none" /> Don't link (tasks only)</label>
                </div>
              </div>

              {/* Fields */}
              <div className="space-y-2">
                <div className="text-sm font-semibold">Suggested fields ({analysis.fields?.length || 0})</div>
                {(analysis.fields || []).map((f, i) => {
                  const st = fieldState[i] || {};
                  const rejected = st.status === "reject";
                  return (
                    <div key={i} className={`rounded-lg border p-3 ${rejected ? "border-white/5 bg-white/[0.02] opacity-60" : "border-white/10 bg-white/5"}`} data-testid={`ui-field-${i}`}>
                      <div className="flex items-start gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[11px] uppercase tracking-wide text-slate-400">{f.category}</span>
                            <span className="text-sm font-semibold">{f.label}</span>
                            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${CONF[f.confidence] || CONF["Needs Review"]}`}>{f.confidence}</span>
                          </div>
                          {st.status === "edit" ? (
                            <input value={st.value} onChange={(e) => setField(i, { value: e.target.value })}
                              className="mt-1 w-full rounded-md bg-white/95 px-2 py-1 text-sm text-slate-900" data-testid={`ui-field-input-${i}`} />
                          ) : (
                            <div className="mt-0.5 text-sm text-slate-100">{st.value}</div>
                          )}
                          {f.source_excerpt && <div className="mt-1 text-[11px] italic text-slate-400">“{f.source_excerpt}”</div>}
                        </div>
                      </div>
                      <div className="mt-2 flex gap-1.5">
                        <button onClick={() => setField(i, { status: "accept" })} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${st.status === "accept" ? "bg-emerald-500/20 text-emerald-200" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`ui-accept-${i}`}><CheckCircle2 className="h-3 w-3" /> Accept</button>
                        <button onClick={() => setField(i, { status: "edit" })} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${st.status === "edit" ? "bg-amber-500/20 text-amber-200" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`ui-edit-${i}`}><Pencil className="h-3 w-3" /> Edit</button>
                        <button onClick={() => setField(i, { status: "reject" })} className={`flex items-center gap-1 rounded-md px-2 py-1 text-[11px] ${st.status === "reject" ? "bg-rose-500/20 text-rose-200" : "bg-white/5 text-slate-300 hover:bg-white/10"}`} data-testid={`ui-reject-${i}`}><Ban className="h-3 w-3" /> Reject</button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Tasks */}
              {tasks.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-sm font-semibold"><ListPlus className="h-4 w-4 text-[#B28A3D]" /> Suggested follow-up tasks</div>
                  {tasks.map((t, i) => (
                    <div key={i} className="rounded-lg border border-white/10 bg-white/5 p-3" data-testid={`ui-task-${i}`}>
                      <label className="flex items-start gap-2">
                        <input type="checkbox" checked={t.include} onChange={(e) => setTasks((p) => p.map((x, k) => k === i ? { ...x, include: e.target.checked } : x))} className="mt-1" data-testid={`ui-task-include-${i}`} />
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold">{t.title}</div>
                          <div className="text-[12px] text-slate-300">{t.reason}</div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="text-slate-400">Due:</span>
                            <input type="date" value={t.due_date} onChange={(e) => setTasks((p) => p.map((x, k) => k === i ? { ...x, due_date: e.target.value } : x))}
                              className="rounded bg-white/95 px-1.5 py-0.5 text-slate-900" data-testid={`ui-task-due-${i}`} />
                            {!t.due_date && <span className="text-amber-300">No due date detected — select one</span>}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              )}

              {analysis.instabrain_recommended && (
                <div className="flex items-center gap-2 rounded-lg border border-[#B28A3D]/40 bg-[#B28A3D]/10 p-3 text-[12px]" data-testid="ui-instabrain">
                  <Sparkles className="h-4 w-4 text-[#d9b978]" />
                  <span>Placement-ready. On approval this is flagged for the existing <strong>manual InstaBrain Placement Review</strong> on the Lead Pipeline. No auto-quoting or carrier sync.</span>
                </div>
              )}

              <label className="flex items-center gap-2 text-[12px] text-slate-300">
                <input type="checkbox" checked={saveNote}
                  onChange={(e) => {
                    if (e.target.checked && analysis.sensitive_detected && !window.confirm("Sensitive info was detected. Save the original content as a restricted private note anyway?")) return;
                    setSaveNote(e.target.checked);
                  }} data-testid="ui-save-note" />
                Save original content as private internal note {analysis.sensitive_detected && <span className="text-amber-300">(off by default — sensitive detected)</span>}
              </label>

              <div className="flex gap-2 pt-1">
                <button onClick={approve} disabled={busy} className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-[#B28A3D] px-4 py-3 text-sm font-semibold text-[#17324D] hover:bg-[#c99b45] disabled:opacity-60" data-testid="ui-approve-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Review &amp; save approved changes
                </button>
                <button onClick={() => api.post(`/universal-intake/${intakeId}/reject`).finally(closeIntake)} className="rounded-lg border border-white/15 px-4 py-3 text-sm hover:bg-white/10" data-testid="ui-discard-btn">Discard</button>
              </div>
            </div>
          )}

          {/* STEP: DONE */}
          {step === "done" && (
            <div className="grid place-items-center gap-3 py-20 text-center" data-testid="ui-done">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
              <div className="font-heading text-lg font-bold">Changes saved</div>
              <p className="max-w-sm text-sm text-slate-300">Approved fields and tasks are now in the platform, and the client timeline was updated. The Fortunate AI Agent will use this data when ranking priorities.</p>
              {analysis?.sensitive_detected && approvedClientId && (
                <div className="w-full max-w-sm rounded-lg border border-[#B28A3D]/40 bg-[#B28A3D]/10 p-3 text-left text-[12px]" data-testid="ui-needs-secure-entry">
                  <div className="flex items-center gap-1.5 font-semibold text-[#e7c987]"><ShieldAlert className="h-4 w-4" /> Sensitive information still needs secure entry</div>
                  <p className="mt-1 text-slate-300">SSN/bank details were <strong>not</strong> stored in normal fields. Add them to the encrypted Restricted Application Data Vault to complete the carrier application.</p>
                </div>
              )}
              <div className="flex flex-wrap justify-center gap-2">
                {approvedClientId && (
                  <button onClick={() => { closeIntake(); openVault({ clientId: approvedClientId, intakeId, tab: "vault" }); }} className="flex items-center gap-1.5 rounded-lg bg-[#17324D] px-4 py-2.5 text-sm font-semibold text-white ring-1 ring-[#B28A3D]/40" data-testid="ui-open-vault"><ShieldAlert className="h-4 w-4 text-[#B28A3D]" /> Restricted Data Vault</button>
                )}
                {approvedClientId && (
                  <button onClick={() => { closeIntake(); openVault({ clientId: approvedClientId, intakeId, tab: "prepare" }); }} className="flex items-center gap-1.5 rounded-lg bg-[#B28A3D] px-4 py-2.5 text-sm font-semibold text-[#17324D]" data-testid="ui-prepare-application"><Sparkles className="h-4 w-4" /> Prepare Application</button>
                )}
                <button onClick={closeIntake} className="rounded-lg border border-white/15 px-5 py-2.5 text-sm font-semibold" data-testid="ui-done-close">Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
